<template>
  <div class="tk">
    <div class="tk-chart">
      <apexchart :key="chartKey" v-if="total > 0" type="radialBar" height="100%" :options="options" :series="parts.map((p) => p.pct)"/>
      <div v-else class="w-empty"><i class="fas fa-coins"></i>{{ ready ? 'No ZCO2 yet' : 'Loading…' }}</div>
    </div>
    <div class="tk-list">
      <div v-for="(p, i) in parts" :key="p.key" class="tk-row w-row-in" :style="{ animationDelay: (i * 0.08) + 's' }">
        <span class="tk-dot" :style="{ background: p.color }"></span>
        <span class="tk-name">{{ p.label }}</span>
        <span class="tk-val"><b>{{ compact(p.value) }}</b> ZCO2</span>
      </div>
      <div class="tk-note" v-if="bufferPercent !== null">Buffer: {{ fmt(bufferPercent) }}% of every mint is kept on-chain as insurance.</div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import { apexBase, compact, fmt, palette, themeState } from '../theme';

export default {
  name: 'TokenFlow',
  components: { apexchart: VueApexCharts },
  props: { stats: { type: Object, required: true } },
  computed: {
    // a theme or data change re-creates the chart: ApexCharts does not always redraw mixed charts on update
    chartKey() { return `${themeState.tick}:${JSON.stringify(this.parts.map((p) => p.pct))}`; },
    ready() { return !!this.stats.tokenFlow; },
    bufferPercent() {
      const live = this.stats.live || {};
      const tf = this.stats.tokenFlow || {};
      const v = live.bufferPercent !== undefined && live.bufferPercent !== null ? live.bufferPercent : tf.bufferPercent;
      return v === undefined || v === null ? null : Number(v);
    },
    parts() {
      const p = palette();
      const tf = this.stats.tokenFlow || {};
      const i = this.stats.insights || {};
      const live = this.stats.live || {};
      const buffer = live.buffer !== undefined && live.buffer !== null ? live.buffer : tf.buffer;
      const list = [
        { key: 'available', label: 'Available in the forests', value: Number(tf.totalAvailable) || 0, color: p.green },
        { key: 'retired', label: 'Retired', value: i.retirements ? i.retirements.total : 0, color: p.coral },
        { key: 'withdrawn', label: 'Withdrawn', value: i.withdrawals ? i.withdrawals.total : 0, color: p.violet },
        // the on-chain buffer is the platform's, not a customer's
        ...(this.stats.scope === 'user' ? [] : [{ key: 'buffer', label: 'On-chain buffer', value: Number(buffer) || 0, color: p.amber }]),
      ];
      const total = list.reduce((a, x) => a + x.value, 0) || 1;
      // the rings show each part against the biggest one, so small parts are still visible
      const max = Math.max(...list.map((x) => x.value)) || 1;
      return list.map((x) => ({ ...x, share: (x.value / total) * 100, pct: Math.max(x.value > 0 ? 4 : 0, Math.round((x.value / max) * 100)) }));
    },
    total() { return this.parts.reduce((a, x) => a + x.value, 0); },
    options() {
      void themeState.tick;
      const p = palette();
      const total = this.total;
      return apexBase({
        labels: this.parts.map((x) => x.label),
        colors: this.parts.map((x) => x.color),
        stroke: { lineCap: 'round' },
        plotOptions: {
          radialBar: {
            hollow: { size: '34%' },
            track: { background: p.border, opacity: 0.5, margin: 5 },
            dataLabels: {
              name: { fontSize: '11px', color: p.muted, offsetY: 16 },
              value: { fontSize: '18px', fontWeight: 800, color: p.heading, offsetY: -12, formatter: (v, o) => (o && o.config ? '' : `${v}%`) },
              total: { show: true, label: 'ZCO2', color: p.muted, fontSize: '11px', formatter: () => compact(total) },
            },
          },
        },
        tooltip: { enabled: false },
      });
    }
  },
  methods: { compact, fmt }
};
</script>

<style scoped lang="scss">
@import '../widgets.scss';

.tk {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 0 14px 12px;
}

.tk-chart { position: relative; flex: 1 1 60%; min-height: 140px; }
.tk-list { flex: 0 0 auto; }

.tk-row {
  display: grid;
  grid-template-columns: 12px 1fr auto;
  gap: 8px;
  align-items: center;
  padding: 3px 4px;
  font-size: 0.78rem;
}

.tk-dot { width: 10px; height: 10px; border-radius: 3px; }
.tk-name { color: var(--text-main); font-weight: 600; }
.tk-val { color: var(--text-muted); font-variant-numeric: tabular-nums; b { color: var(--text-heading); } }
.tk-note { margin-top: 6px; font-size: 0.68rem; color: var(--text-muted); }
</style>
